// Modais das parcelas do pedido de compra: o aviso de que a mudança de data
// recalculou vencimentos e o aviso pós-salvamento das parcelas pagas com
// cartão que não puderam ir para a fatura.

import React from 'react';
import { CalendarClock, AlertTriangle } from 'lucide-react';
import { Button } from '@components/Button';
import { Dialog } from '@components/Dialog';
import { formatDateBR } from '@pages/financial/financialUtils';
import type { DueDateChange } from './paymentTermUtils';

export const DueDatesUpdatedModal: React.FC<{
  changes: DueDateChange[] | null;
  /** Qual data mudou: "Data Prevista" ou "Data do Pedido". */
  fieldLabel: string;
  onClose: () => void;
}> = ({ changes, fieldLabel, onClose }) => (
  <Dialog
    isOpen={!!changes && changes.length > 0}
    onClose={onClose}
    title="Parcelas atualizadas"
    size="md"
    footer={<Button onClick={onClose}>Entendi</Button>}
  >
    <div className="space-y-3 text-sm">
      <p className="flex items-start gap-2 text-slate-700">
        <CalendarClock size={18} className="text-emerald-600 shrink-0 mt-0.5" />
        <span>
          A {fieldLabel} mudou, e os vencimentos das parcelas calculadas pela condição de pagamento do fornecedor foram
          ajustados. Os valores não mudam.
        </span>
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="py-1.5 pr-2">Parcela</th>
            <th className="py-1.5 pr-2">Vencimento anterior</th>
            <th className="py-1.5">Novo vencimento</th>
          </tr>
        </thead>
        <tbody>
          {(changes ?? []).map((c) => (
            <tr key={c.n} className="border-b border-slate-100">
              <td className="py-1.5 pr-2">{c.n}ª</td>
              <td className="py-1.5 pr-2 text-slate-500 line-through">{formatDateBR(c.before)}</td>
              <td className="py-1.5 font-medium text-emerald-700">{formatDateBR(c.after)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-slate-500">
        A alteração só é gravada quando você salvar o pedido. Parcelas com data fixa (editadas à mão) não foram alteradas.
      </p>
    </div>
  </Dialog>
);

export const SaveWarningsModal: React.FC<{
  warnings: string[] | null;
  onClose: () => void;
}> = ({ warnings, onClose }) => (
  <Dialog
    isOpen={!!warnings && warnings.length > 0}
    onClose={onClose}
    title="Pedido salvo com avisos"
    size="md"
    footer={<Button onClick={onClose}>OK</Button>}
  >
    <div className="space-y-2 text-sm">
      {(warnings ?? []).map((w, i) => (
        <p key={i} className="flex items-start gap-2 text-amber-800">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <span>{w}</span>
        </p>
      ))}
    </div>
  </Dialog>
);
